import { memo, useEffect, useState } from 'react';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import instance from 'services/api';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import Preloader from 'components/Preloader';
import { SuperAdminLoadingButton } from 'redesign/Shared/styles';
import { updateCurrentBusiness } from 'redux/actions/business';
import { BUSINESS_TYPE_CATEGORY } from 'constants/companyTypes';
import ConfirmPromptModal from 'components/ConfirmPromptModal/ConfirmPromptModal';
import {
  BusinessSettingsSubTitle,
  BusinessSettingsTitle,
} from 'pages/Settings/BusinessSettings/styles';

const businessTypesSchema = yup.object().shape({
  businessTypes: yup
    .array()
    .of(yup.string())
    .min(1, 'settings.business_types_required'),
});

const availableTypes = Object.values(BUSINESS_TYPE_CATEGORY);

const toFormValues = (businessTypes) => ({
  businessTypes: Array.isArray(businessTypes) ? businessTypes : [],
});

const BusinessTypesSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const { currentBusiness, isBrandLoading } = useSelector(
    ({ business }) => business
  );

  const businessTypesForm = useForm({
    defaultValues: toFormValues(currentBusiness?.businessTypes),
    mode: 'all',
    resolver: yupResolver(businessTypesSchema),
  });

  useEffect(() => {
    businessTypesForm.reset(toFormValues(currentBusiness?.businessTypes), {
      keepDirty: false,
    });
  }, [currentBusiness?.businessTypes]);

  const handleBusinessTypesSubmit = (values) => {
    setLoading(true);
    return instance
      .patch('/business/business-types', {
        businessTypes: values.businessTypes,
      })
      .then((res) => {
        const saved = res?.data?.data?.businessTypes || values.businessTypes;
        businessTypesForm.reset(toFormValues(saved), { keepDirty: false });
        dispatch(
          updateCurrentBusiness({
            ...currentBusiness,
            businessTypes: saved,
          })
        );
        setLoading(false);
        toast.success('Setting is successfully saved');
      })
      .catch(() => setLoading(false));
  };

  const errorMessage =
    businessTypesForm.formState.errors?.businessTypes?.message;

  return (
    <>
      {isBrandLoading && <Preloader />}
      {!isBrandLoading && (
        <FormProvider {...businessTypesForm}>
          <ConfirmPromptModal
            handleConfirm={businessTypesForm.handleSubmit(
              handleBusinessTypesSubmit
            )}
            submitData={businessTypesForm.getValues()}
            hasUnsavedChanges={businessTypesForm.formState.isDirty}
            formState={businessTypesForm}
          />
          <form
            onSubmit={businessTypesForm.handleSubmit(handleBusinessTypesSubmit)}
          >
            <Grid container>
              <Grid item xs={12}>
                <BusinessSettingsTitle>
                  {t('settings.business_types_settings')}
                </BusinessSettingsTitle>
                <BusinessSettingsSubTitle>
                  {t('settings.business_types_settings_description')}
                </BusinessSettingsSubTitle>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="businessTypes"
                  control={businessTypesForm.control}
                  render={({ field }) => (
                    <FormGroup>
                      {availableTypes.map((type) => {
                        const selected = field.value || [];
                        return (
                          <FormControlLabel
                            key={type}
                            label={t(`settings.business_type_${type}`)}
                            control={
                              <Checkbox
                                checked={selected.includes(type)}
                                onChange={(event) =>
                                  field.onChange(
                                    event.target.checked
                                      ? [...selected, type]
                                      : selected.filter((item) => item !== type)
                                  )
                                }
                              />
                            }
                          />
                        );
                      })}
                    </FormGroup>
                  )}
                />
                {errorMessage && (
                  <FormHelperText error>{t(errorMessage)}</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12}>
                <SuperAdminLoadingButton
                  id="businessTypesSettingsLoading"
                  disabled={!businessTypesForm.formState.isDirty || loading}
                  variant="contained"
                  type="submit"
                  sx={{ width: '200px', mt: 2, alignItems: 'right', p: 1 }}
                  loading={loading}
                >
                  {t('settings.save')}
                </SuperAdminLoadingButton>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      )}
    </>
  );
};

export default memo(BusinessTypesSettings);
